import { useState } from 'react';
import { navLinks, profile } from '../../data/site';
import { LinkedInIcon, MailIcon, PhoneIcon } from '../icons';
import { useActiveSection, useScrolled } from '../../hooks/useScrollFx';
import { useTheme } from '../../hooks/useTheme';
import ThemeToggle from './ThemeToggle';

const SECTION_IDS = ['home', ...navLinks.map((l) => l.id)];

function scrollToId(id) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

/*--------------------
* Header
----------------------*/
export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const stuck = useScrolled(40);
  const active = useActiveSection(SECTION_IDS);
  const { theme, toggle } = useTheme();

  const go = (id) => {
    setMenuOpen(false);
    scrollToId(id);
  };

  return (
    <header
      className={`site-header ${stuck ? 'is-stuck' : ''} ${menuOpen ? 'is-open' : ''}`}
    >
      {/* Contact strip collapses on scroll */}
      <div className={`utility-bar ${stuck ? 'is-hidden' : ''}`}>
        <div className="container">
          <div className="flex items-center justify-between gap-4 py-2.5">
            <div className="flex items-center gap-4 sm:gap-6">
              <a className="utility-link inline-flex" href={`tel:${profile.phone.replace(/\s/g, '')}`}>
                <PhoneIcon />
                <span>{profile.phone}</span>
              </a>
              <a className="utility-link inline-flex" href={`mailto:${profile.email}`}>
                <MailIcon />
                <span className="hidden sm:inline">{profile.email}</span>
                <span className="sm:hidden">Email</span>
              </a>
            </div>

            {/* The availability badge already lives in the hero this slot
                carries LinkedIn instead so nothing is said twice. */}
            <a
              className="utility-link hidden md:inline-flex"
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
            >
              <LinkedInIcon />
              <span>LinkedIn</span>
              <span aria-hidden="true">&#8599;</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="container">
        <div className="flex items-center justify-between py-3.5">
          <button
            onClick={() => go('home')}
            className="font-display text-[17px] font-[600] tracking-[-.02em] text-chalk transition-opacity duration-200 hover:opacity-70 lg:text-[18px]"
            aria-label="Back to top"
          >
            Dikshit Patoliya
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:block">
            <ul className="flex items-center gap-1">
              {navLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => go(link.id)}
                    className={`nav-link ${active === link.id ? 'is-active' : ''}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* Desktop actions */}
          <div className="hidden items-center gap-2.5 lg:flex">
            <ThemeToggle theme={theme} onToggle={toggle} />
            <a
              className="btn btn-ghost btn-sm"
              href={`${process.env.PUBLIC_URL}/${profile.cv}`}
              target="_blank"
              rel="noreferrer"
            >
              Resume
            </a>
            <button className="btn btn-primary btn-sm" onClick={() => go('contact')}>
              Let&rsquo;s talk
            </button>
          </div>

          {/* Mobile actions */}
          <div className="flex items-center gap-2 lg:hidden">
            <ThemeToggle theme={theme} onToggle={toggle} />
            <button
              className={`burger flex h-9 w-9 flex-col items-center justify-center gap-[5px] ${
                menuOpen ? 'is-open' : ''
              }`}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        {/* Mobile drawer */}
        <div className={`mobile-drawer ${menuOpen ? 'is-open' : ''}`}>
          <ul className="flex flex-col gap-1 border-t border-tint/[.07] py-4">
            {navLinks.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => go(link.id)}
                  className={`w-full rounded-lg px-3 py-2.5 text-left text-[15px] transition-colors ${
                    active === link.id
                      ? 'bg-tint/[.05] text-chalk'
                      : 'text-muted hover:text-chalk'
                  }`}
                >
                  {link.label}
                </button>
              </li>
            ))}
            <li className="mt-3 flex gap-3">
              <a
                className="btn btn-ghost btn-sm flex-1"
                href={`${process.env.PUBLIC_URL}/${profile.cv}`}
                target="_blank"
                rel="noreferrer"
              >
                Resume
              </a>
              <button className="btn btn-primary btn-sm flex-1" onClick={() => go('contact')}>
                Let&rsquo;s talk
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  );
}
